"use client"

import React from 'react'
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { useCodeValues } from "@/hooks/use-code-values"
import { BillingTransaction } from '@/hooks/use-billing-transaction-reports'
import { cn } from "@/lib/utils"
import { useFormatters } from "@/hooks/use-formatters"
import { FileText } from "lucide-react"
import { ParentNavigationLink } from '../ui/parent-navigation-link'
import { SortIcon, getAriaSortValue } from '@/components/ui/sort-icon'

// Billing Transaction Status constants
const BILLING_TRANSACTION_STATUS = {
  DRAFT: 1,
  PENDING_TRANSACTION_CLEARING: 2,
  INVOICED: 3,
  SUPPRESSED: 4,
  ACTIVE: 5,
  PENDING_INVOICE_CLEARING: 6
} as const

// Invoice Status constants  
const INVOICE_STATUS = {
  DRAFT: 1,
  CANCELLED: 2,
  PENDING_CLIENT_PAYMENT: 3,
  PAID: 4
} as const

interface BillingTransactionsContentProps {
  billingTransactions: BillingTransaction[]
  loading?: boolean
  error?: string | null
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  onSort?: (field: string) => void
}

// Helper function to get status badge className based on billing transaction status ID
const getBillingStatusClassName = (statusId: number): string => {
  switch (statusId) {
    case BILLING_TRANSACTION_STATUS.INVOICED:
    case BILLING_TRANSACTION_STATUS.ACTIVE:
      return "bg-green-100 text-green-800 hover:bg-green-100"
    case BILLING_TRANSACTION_STATUS.SUPPRESSED:
      return "bg-red-100 text-red-800 hover:bg-red-100"
    case BILLING_TRANSACTION_STATUS.DRAFT:
      return "bg-orange-100 text-orange-800 hover:bg-orange-100"
    case BILLING_TRANSACTION_STATUS.PENDING_TRANSACTION_CLEARING:
    case BILLING_TRANSACTION_STATUS.PENDING_INVOICE_CLEARING:
      return "bg-blue-100 text-blue-800 hover:bg-blue-100"
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100"
  }
}

// Helper function to get invoice status badge className based on invoice status ID
const getInvoiceStatusClassName = (statusId: number): string => {
  switch (statusId) {
    case INVOICE_STATUS.PAID:
      return "bg-green-100 text-green-800 hover:bg-green-100"
    case INVOICE_STATUS.PENDING_CLIENT_PAYMENT:
      return "bg-blue-100 text-blue-800 hover:bg-blue-100"
    case INVOICE_STATUS.CANCELLED:
      return "bg-red-100 text-red-800 hover:bg-red-100"
    case INVOICE_STATUS.DRAFT:
      return "bg-orange-100 text-orange-800 hover:bg-orange-100"
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100"
  }
}

export function BillingTransactionsContent({ billingTransactions, loading = false, error, sortField, sortDirection, onSort }: BillingTransactionsContentProps) {
  const { getCodeValueText, getCodeValues } = useCodeValues()
  const { formatCurrency, formatDate } = useFormatters()

  const getCurrencyCode = (currencyId: number): string => {
    const currencies = getCodeValues('geo.CodeCurrency', false)
    const currency = currencies.find(c => c.id === currencyId)
    return currency?.code || currencyId.toString()
  }

  const renderSortIcon = (field: string) => (
    <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-slate-600">
          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          <span>Loading billing transactions...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-red-600">
        <p>Error loading billing transactions: {error}</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <Table className="w-full">
        <TableHeader>
          <TableRow className="border-b border-slate-200 bg-slate-50/50">
            <TableHead
              className="font-semibold text-slate-700"
              aria-sort={getAriaSortValue('EntityId', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('EntityId')}>
                ID
                {renderSortIcon('EntityId')}
              </button>
            </TableHead>
            <TableHead
              className="font-semibold text-slate-700 whitespace-nowrap"
              aria-sort={getAriaSortValue('BillingTransactionNumber', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('BillingTransactionNumber')}>
                Transaction #
                {renderSortIcon('BillingTransactionNumber')}
              </button>
            </TableHead>
            <TableHead
              className="font-semibold text-slate-700"
              aria-sort={getAriaSortValue('BillingTransactionStatusId', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('BillingTransactionStatusId')}>
                Status
                {renderSortIcon('BillingTransactionStatusId')}
              </button>
            </TableHead>
            <TableHead className="font-semibold text-slate-700">Type</TableHead>
            <TableHead
              className="text-right font-semibold text-slate-700 whitespace-nowrap"
              aria-sort={getAriaSortValue('TotalAmount', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center justify-end w-full hover:text-slate-900" onClick={() => onSort?.('TotalAmount')}>
                Amount
                {renderSortIcon('TotalAmount')}
              </button>
            </TableHead>
            <TableHead
              className="font-semibold text-slate-700"
              aria-sort={getAriaSortValue('BillingDate', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('BillingDate')}>
                Billing Date
                {renderSortIcon('BillingDate')}
              </button>
            </TableHead>
            <TableHead className="font-semibold text-slate-700">Invoice Number</TableHead>
            <TableHead
              className="font-semibold text-slate-700"
              aria-sort={getAriaSortValue('InvoiceStatusId', sortField, sortDirection)}
            >
              <button type="button" className="flex items-center hover:text-slate-900" onClick={() => onSort?.('InvoiceStatusId')}>
                Invoice Status
                {renderSortIcon('InvoiceStatusId')}
              </button>
            </TableHead>
            <TableHead className="font-semibold text-slate-700 min-w-32">Note</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {billingTransactions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={100} className="text-center py-12">
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                    <FileText className="w-8 h-8 text-slate-400" />
                  </div>
                  <div className="text-slate-500">No billing transactions found</div>
                  <p className="text-sm text-slate-400">There are no billing transactions available for this engagement</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            billingTransactions.map((transaction, index) => {
              const currencyCode = getCurrencyCode(transaction.CurrencyId)
              return (
                <TableRow
                  key={transaction.EntityId}
                  className={cn(
                    "border-b border-slate-100 hover:bg-blue-50/50 transition-colors",
                    index % 2 === 0 ? "bg-white" : "bg-slate-50/30",
                  )}
                >
                  <TableCell className="font-mono text-sm text-slate-600">
                    {transaction.EntityId}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    <ParentNavigationLink route={`/next/transaction/${transaction.TransactionHeaderId}/summary`}>
                      {transaction.BillingTransactionNumber}
                    </ParentNavigationLink>
                  </TableCell>
                  <TableCell>
                    <Badge className={cn("font-medium whitespace-nowrap", getBillingStatusClassName(transaction.BillingTransactionStatusId))}>
                      {getCodeValueText(transaction.BillingTransactionStatusId, 'trn.CodeBillingTransactionStatus')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {getCodeValueText(transaction.TransactionTypeId, 'trn.CodeTransactionType')}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-slate-900 whitespace-nowrap">
                    {currencyCode} {formatCurrency(transaction.TotalAmount, currencyCode)}
                  </TableCell>
                  <TableCell className="text-slate-700 whitespace-nowrap">
                    {formatDate(transaction.BillingDate, '-')}
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {transaction.InvoiceNumber}
                  </TableCell>
                  <TableCell>
                    {transaction.InvoiceStatusId && (
                      <Badge className={cn("font-medium whitespace-nowrap", getInvoiceStatusClassName(transaction.InvoiceStatusId))}>
                        {getCodeValueText(transaction.InvoiceStatusId, 'inv.CodeInvoiceStatus')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-700">
                    {transaction.InvoiceNote1 || ''}
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  )
}
